import { useRef } from "react";
import { Button, Modal } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useReactToPrint } from "react-to-print";
import { useSelector } from "react-redux";
import { convertToArabicDate } from "../../utils/date";
import { formatDateTime } from "../../utils/constants";
import { IMAGE_URL } from "../../constants";
import Logo from "../../assets/images/LOGOS-03.png";
import LogoStationnnement from "../../assets/images/stationnement-logo.png";
import PermissionRender from "../PermissionRender";

const WarningPdf = ({ formValues, visible, onClose, screenshot }) => {
  const pdfRef = useRef();
  const user = useSelector((state) => state.user.user);

  const getCurrentDate = () => {
    return new Date().toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const pageStyle = `
    @page {
      size: A4;
      margin: 15mm;
    }
    @media print {
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        width: 210mm;
        height: 297mm;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      .printable-area {
        width: 100%;
        height: auto;
        margin: 0 auto !important;
        padding: 10mm !important;
        border: none !important;
        box-shadow: none !important;
        overflow: visible !important;
        font-size: 10pt !important;
        line-height: 1.4 !important;
        font-family: Arial, sans-serif !important;
      }
      .printable-area * {
        color: #000 !important;
      }
      .printable-area h2 {
        font-size: 18pt !important;
        margin-top: 8pt !important;
        margin-bottom: 8pt !important;
        font-weight: bold !important;
        text-decoration: underline !important;
      }
      .printable-area p {
        font-size: 14pt !important;
        margin: 4pt 0 !important;
        font-weight: 500 !important;
      }
    
      .printable-area h6 {
        font-size: 14pt !important;
        font-weight: 600 !important;
      }
      .printable-area strong {
        font-weight: bold !important;
      }
      
      .page-break {
        page-break-before: always !important;
        break-before: page !important;
      }
      
      .page-1, .page-2, .page-3 {
        min-height: 250mm;
        width: 100%;
      }
      
      .pdf-header {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        height: 70px;
        background: white;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #ddd;
        z-index: 1000;
      }
      
      .pdf-header img {
        height: 70px !important;
        width: auto !important;
      }
      
      .pdf-footer {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        height: 15px;
        background: white;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 5px 20px;
        border-top: 1px solid #ddd;
        font-size: 10pt !important;
        z-index: 1000;
      }
      
      .page-content {
        margin-top: 67px;
        margin-bottom: 40px !important;
      }

      .page-1.page-content {
        margin-top: 33px !important;
      }
      
      .images-page {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 40mm;
        margin-top: 20px !important;
        padding-top: 20px !important;
      }
      
      .image-row {
        display: flex;
        justify-content: center;
        gap: 15mm;
        width: 100%;
        page-break-inside: avoid;
      }
      
      .image-row:nth-child(2) {
        page-break-after: always;
      }
      
      .infraction-image {
        width: 280px !important;
        height: auto !important;
        border: 2px solid #ccc;
        border-radius: 8px;
      }
      
      .images-continuation-page {
        margin-top: 100px !important;
        padding-top: 30px !important;
      }
      
      .no-print {
        display: none !important;
      }
    }
  `;

  const printNow = useReactToPrint({
    contentRef: pdfRef,
    documentTitle: `Avertissement_${formValues?.reference + "_"}A4`,
    pageStyle: pageStyle,
  });

  const handlePrint = () => {
    setTimeout(() => {
      if (pdfRef.current) {
        printNow();
      } else {
        console.error("PDF reference is not available.");
      }
    }, 200);
  };

  const date = formValues?.datetime_warning?.split(" ")[0];
  const time = formValues?.datetime_warning?.split(" ")[1];

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width="700px"
      centered
    >
      <div className="mb-4 text-left no-print">
        <PermissionRender requiredPermission={["warnings", "print_warning"]}>
          <Button
            onClick={handlePrint}
            type="primary"
            icon={<DownloadOutlined />}
          >
            طباعة
          </Button>
        </PermissionRender>
      </div>

      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          overflow: "hidden",
          paddingBottom: "20px",
        }}
      >
        <div
          style={{
            transform: "scale(0.85)",
            transformOrigin: "top center",
          }}
        >
          <div
            ref={pdfRef}
            className="printable-area bg-white text-black"
            dir="rtl"
            style={{
              fontFamily: "Arial, sans-serif",
              fontSize: "14px",
              margin: "0",
              padding: "40px",
              border: "1px solid #ccc",
              backgroundColor: "white",
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            {/* Header with Logo */}
            <div className="pdf-header">
              <img src={Logo} alt="App Logo" style={{ width: "10px" }} />
              <img
                src={LogoStationnnement}
                alt="App Logo"
                style={{ width: "10px" }}
              />
            </div>

            {/* PAGE 1 - Arabic & French Warning Report */}
            <div className="page-1 page-content">
              {/* Arabic Header */}
              <h2
                style={{
                  textAlign: "center",
                  fontSize: "22px",
                  fontWeight: "bold",
                  margin: "0",
                }}
              >
                مدينة طنجة
              </h2>
              <h2
                style={{
                  textAlign: "center",
                  fontSize: "22px",
                  fontWeight: "bold",
                  margin: "0",
                }}
              >
                خدمات الركن
              </h2>
              <p
                style={{
                  textAlign: "center",
                  fontSize: "16px",
                  marginTop: "10px",
                  marginBottom: "0",
                }}
              >
                VILLE DE TANGER
              </p>
              <p
                style={{
                  textAlign: "center",
                  fontSize: "16px",
                  margin: "0",
                }}
              >
                SERVICE DE STATIONNEMENT
              </p>

              {/* Arabic Title */}
              <h2
                style={{
                  textAlign: "center",
                  fontSize: "22px",
                  fontWeight: "bold",
                  marginTop: "30px",
                  marginBottom: "15px",
                  textDecoration: "underline",
                }}
              >
                إعذار
              </h2>

              {/* Arabic Body */}
              <p
                style={{
                  textAlign: "right",
                  fontSize: "15px",
                  lineHeight: 1.8,
                  marginTop: "20px",
                }}
              >
                سياراتكم مركونة في المنطقة الزرقاء (الركن المؤدى عنه) التي تخضع
                إلى مقتضيات القرار الجماعي عدد 1357/2022. المرجو أن تتفضلوا
                الأداء في مدة أقصاها عشر (10) دقائق عن طريق العداد الآلى الموجود
                بالقرب من مكان توقف سياراتكم لتجنب المخالفات المنصوص عليها في
                القرار المشار إليه أعلاه.
              </p>

              {/* Arabic Fields */}
              <div
                style={{
                  textAlign: "right",
                  fontSize: "15px",
                  lineHeight: 2,
                  marginTop: "20px",
                }}
              >
                <p style={{ margin: "4px 0" }}>
                  <strong>اسم العون المحلف: </strong>
                  <strong>{formValues?.user?.full_name_ar}</strong>
                </p>
                <p style={{ margin: "4px 0" }}>
                  <strong>التاريخ والساعة: </strong>
                  <strong>
                    {convertToArabicDate(date)} {time}
                  </strong>
                </p>
                <p style={{ margin: "4px 0" }}>
                  <strong>المنطقة: </strong>
                  <strong>
                    {formValues?.location_ar ||
                      formValues?.street?.name_ar ||
                      "غير محدد"}
                  </strong>
                </p>
                <p style={{ margin: "4px 0" }}>
                  <strong>رقم لوحة السيارة : </strong>
                  <strong dir="ltr" style={{ display: "inline-block" }}>
                    {formValues?.matricule}
                  </strong>
                </p>
                <p style={{ margin: "4px 0" }}>
                  <strong>نوع المخالفة : </strong>
                  <strong>{formValues?.type_infraction?.name_ar}</strong>
                </p>
                <p style={{ margin: "4px 0" }}>
                  <strong>رقم الوصل: </strong>
                  <strong>{formValues?.reference}</strong>
                </p>
              </div>

              {/* French Title */}
              <h2
                style={{
                  textAlign: "center",
                  fontSize: "18px",
                  fontWeight: "bold",
                  marginTop: "30px",
                  marginBottom: "15px",
                  textDecoration: "underline",
                }}
                dir="ltr"
              >
                AVERTISSEMENT
              </h2>

              {/* French Body */}
              <p
                dir="ltr"
                style={{
                  textAlign: "left",
                  fontSize: "14px",
                  lineHeight: 1.8,
                  marginTop: "20px",
                }}
              >
                Votre véhicule est stationné en ZONE BLEUE (stationnement
                payant). Cette zone est réglementée par l'Arrêté Communal n°
                1357/2022. Prière procéder au paiement à travers l'horodateur le
                plus proche du lieu de stationnement de votre véhicule dans les
                10 minutes afin d'éviter l'infraction prévue dans l'arrêté
                communal susvisé.
              </p>

              {/* French Fields */}
              <div
                dir="ltr"
                style={{
                  textAlign: "left",
                  fontSize: "14px",
                  lineHeight: 2,
                  marginTop: "20px",
                }}
              >
                <p style={{ margin: "4px 0" }}>
                  <strong>Nom de l'Agent Assermenté : </strong>
                  <strong>{formValues?.user?.full_name}</strong>
                </p>
                <p style={{ margin: "4px 0" }}>
                  <strong>Date et heure : </strong>
                  <strong>
                    {formatDateTime(formValues?.datetime_warning)}
                  </strong>
                </p>
                <p style={{ margin: "4px 0" }}>
                  <strong>Localisation : </strong>
                  <strong>
                    {formValues?.location ||
                      formValues?.street?.name ||
                      "Non défini"}
                  </strong>
                </p>
                <p style={{ margin: "4px 0" }}>
                  <strong>Plaque d'immatriculation : </strong>
                  <strong>{formValues?.matricule}</strong>
                </p>
                <p style={{ margin: "4px 0" }}>
                  <strong>Nature de l'infraction : </strong>
                  <strong>{formValues?.type_infraction?.name}</strong>
                </p>
                <p style={{ margin: "4px 0" }}>
                  <strong>Numéro de reçu : </strong>
                  <strong>{formValues?.reference}</strong>
                </p>
              </div>
            </div>

            {/* PAGE 2 - Screenshot of Warning Details */}
            <div className="page-break page-2 page-content" dir="ltr">
              <div
                style={{
                  textAlign: "center",
                  marginBottom: "20px",
                  marginTop: "10px",
                }}
              >
                <h2
                  style={{
                    fontSize: "20px",
                    fontWeight: "bold",
                    color: "#722ed1",
                  }}
                >
                  Détails de l'Avertissement - Capture d'écran
                </h2>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "250mm",
                  width: "100%",
                }}
              >
                {screenshot ? (
                  <img
                    src={screenshot}
                    alt="Screenshot of Warning Details"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "230mm",
                      objectFit: "contain",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      fontSize: "16px",
                      color: "#666",
                      padding: "50px",
                    }}
                  >
                    Chargement de la capture d'écran...
                  </div>
                )}
              </div>
            </div>

            {/* PAGE 3+ - Images (2 per page) */}
            <div className="page-break page-3 page-content">
              <div style={{ textAlign: "center", marginBottom: "30px" }}>
                <h2
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: "#722ed1",
                  }}
                >
                  Evidence Photos
                </h2>
              </div>

              <div className="images-page">
                {formValues?.photos ? (
                  (() => {
                    const photos = formValues.photos.split(",");
                    const elements = [];

                    for (let i = 0; i < photos.length; i += 2) {
                      const photo1 = photos[i];
                      const photo2 = photos[i + 1];

                      if (i > 0) {
                        elements.push(
                          <div
                            key={`page-break-${i}`}
                            className="page-break images-continuation-page"
                          >
                            <div
                              style={{
                                textAlign: "center",
                                marginBottom: "30px",
                              }}
                            >
                              <h2
                                style={{
                                  fontSize: "24px",
                                  fontWeight: "bold",
                                  color: "#722ed1",
                                }}
                              >
                                Evidence Photos (Page {Math.floor(i / 2) + 3})
                              </h2>
                            </div>
                          </div>
                        );
                      }

                      elements.push(
                        <div key={`row-${i}`} className="image-row">
                          <div style={{ textAlign: "center" }}>
                            <img
                              src={IMAGE_URL + photo1}
                              alt={`Evidence ${i + 1}`}
                              className="infraction-image"
                              style={{
                                width: "280px",
                                objectFit: "contain",
                                border: "2px solid #ccc",
                                borderRadius: "8px",
                              }}
                            />
                            <p
                              style={{
                                fontSize: "14px",
                                marginTop: "10px",
                                fontWeight: "bold",
                              }}
                            >
                              Photo {i + 1}
                            </p>
                          </div>

                          {photo2 && (
                            <div style={{ textAlign: "center" }}>
                              <img
                                src={IMAGE_URL + photo2}
                                alt={`Evidence ${i + 2}`}
                                className="infraction-image"
                                style={{
                                  width: "280px",
                                  objectFit: "contain",
                                  border: "2px solid #ccc",
                                  borderRadius: "8px",
                                }}
                              />
                              <p
                                style={{
                                  fontSize: "14px",
                                  marginTop: "10px",
                                  fontWeight: "bold",
                                }}
                              >
                                Photo {i + 2}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    }

                    return elements;
                  })()
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      fontSize: "16px",
                      color: "#666",
                    }}
                  >
                    No photos available for this warning
                  </div>
                )}
              </div>
            </div>
            <div className="pdf-footer">
              <span>طُبع بواسطة: {user?.full_name || "المستخدم"}</span>
              <span>تاريخ الطباعة: {getCurrentDate()}</span>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default WarningPdf;
